import { FeatureFlag } from '@/constants/featureFlags';
import type { SessionReplayScope } from '@/constants/sessionReplay';
import { mixpanelService } from '@/services/mixpanelService';
import { getBoot, hasBootParam } from '@/utils/boot';
import { deferIdle } from '@/utils/defer';

/**
 * Session Replay recording is gated behind the per-publisher `recordingFlag`
 * (each checkout route passes its own), read from the authoritative
 * (cached) boot promise rather than React state so the value is correct
 * regardless of whether boot has resolved by the time this idle callback
 * runs. Crucially, `initialize()` must ALWAYS run so buffered events flush
 * through the SDK — so we only await `getBoot()` when a boot param exists
 * (otherwise `getBoot()` never settles by design). Fail closed (no
 * recording) when boot is absent or can't be resolved. Event tracking is
 * unaffected either way. `sessionReplayScope` selects the sampling-percent env
 * var for the route.
 */
export const initMixpanel = (recordingFlag: FeatureFlag, sessionReplayScope: SessionReplayScope = 'checkout'): void => {
  deferIdle(() => {
    void resolveSessionRecordingFlag(recordingFlag).then((sessionRecordingEnabled) =>
      mixpanelService.initialize({ sessionRecordingEnabled, sessionReplayScope })
    );
  });
};

const resolveSessionRecordingFlag = (recordingFlag: FeatureFlag): Promise<boolean> =>
  hasBootParam()
    ? getBoot()
        .then((boot) => Boolean(boot?.featureFlags?.[recordingFlag]))
        .catch(() => false)
    : Promise.resolve(false);
